import { useEffect, useState } from "react";
import { Loader2, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import DocPreview from "@/components/DocPreview";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/meta";

const field = "bg-zinc-900/70 border-zinc-800 text-sm";

const SAMPLE = {
  title: "Sample Proposal",
  currency: "INR",
  gst_mode: "cgst_sgst",
  client: { name: "Acme Corp" },
  blocks: [
    { id: "b1", type: "cover", data: { title: "Growth Partnership Proposal", subtitle: "Prepared for your client", prepared_by: "Your team" } },
    { id: "b2", type: "heading", data: { text: "Why now", level: 2 } },
    { id: "b3", type: "text", data: { html: "<p>A short paragraph showing how body copy reads with your brand fonts and colours applied.</p>" } },
    { id: "b4", type: "bullets", data: { items: ["Clear deliverables", "Transparent pricing", "Weekly reporting"] } },
    {
      id: "b5",
      type: "pricing",
      data: {
        title: "Investment",
        gst_mode: "cgst_sgst",
        lines: [
          { id: "l1", name: "Strategy sprint", description: "4 weeks", qty: 1, unit_price_minor: 25000000, discount_pct: 0, tax_pct: 18 },
          { id: "l2", name: "Monthly retainer", description: "3 months", qty: 3, unit_price_minor: 15000000, discount_pct: 5, tax_pct: 18 },
        ],
      },
    },
    { id: "b6", type: "signature", data: { prompt: "By accepting, you agree to the scope and commercials above.", signer_role: "Authorised signatory" } },
  ],
};

export default function BrandKits() {
  const { user } = useAuth();
  const [kits, setKits] = useState(null);
  const [meta, setMeta] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/brand-kits").then((r) => {
      setKits(r.data);
      setActive(r.data[0] || null);
    });
    api.get("/brand-kits/meta").then((r) => setMeta(r.data));
  }, []);

  const set = (k, v) => setActive({ ...active, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...active };
      delete payload.id;
      delete payload.org_id;
      delete payload.created_at;
      const { data } = active.id ? await api.put(`/brand-kits/${active.id}`, payload) : await api.post("/brand-kits", payload);
      if (!active.id) track("BrandKitSaved", { content_name: payload.name });
      const list = (await api.get("/brand-kits")).data;
      setKits(list);
      setActive(list.find((k) => k.id === data.id) || data);
      toast.success("Brand kit saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addKit = () =>
    setActive({
      name: "New brand kit",
      logo_url: "",
      accent_color: "#6366F1",
      text_color: "#18181B",
      background_color: "#FFFFFF",
      heading_font: "Plus Jakarta Sans",
      body_font: "Inter",
      page_size: "A4",
      margin_mm: 18,
      cover_style: "editorial",
      header_text: "",
      footer_text: "Confidential proposal",
      show_page_numbers: true,
      is_default: false,
    });

  const remove = async (id) => {
    try {
      await api.delete(`/brand-kits/${id}`);
      const list = (await api.get("/brand-kits")).data;
      setKits(list);
      setActive(list[0] || null);
      toast.success("Brand kit deleted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!kits || !meta || !active)
    return (
      <AppShell title="Brand Kits">
        <div className="h-48 grid place-items-center text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </AppShell>
    );

  return (
    <AppShell
      title="Brand Kits"
      subtitle="Design your document once, reuse it everywhere"
      actions={
        <Button data-testid="new-brand-kit-btn" variant="outline" onClick={addKit} className="h-9 border-zinc-800 text-zinc-300">
          <Plus className="w-4 h-4 mr-1.5" /> New kit
        </Button>
      }
    >
      <div className="flex flex-wrap gap-2 mb-5">
        {kits.map((k) => (
          <button
            key={k.id}
            data-testid={`brand-kit-tab-${k.id}`}
            onClick={() => setActive(k)}
            className={`px-3 h-9 rounded-lg text-xs border flex items-center gap-2 transition-colors ${
              active.id === k.id ? "border-indigo-500/40 bg-indigo-500/12 text-indigo-300" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            <span className="w-3 h-3 rounded-full" style={{ background: k.accent_color }} />
            {k.name}
            {k.is_default && <Star className="w-3 h-3 text-amber-400" />}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nv-card p-5 space-y-4">
          <div>
            <label className="text-xs text-zinc-500">Kit name</label>
            <Input data-testid="brand-kit-name-input" className={`mt-1 ${field}`} value={active.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Logo URL</label>
            <Input data-testid="brand-kit-logo-input" className={`mt-1 ${field}`} value={active.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…/logo.png" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              ["accent_color", "Accent", "brand-kit-color-picker-accent"],
              ["text_color", "Text", "brand-kit-color-picker-text"],
              ["background_color", "Background", "brand-kit-color-picker-bg"],
            ].map(([key, label, testid]) => (
              <div key={key}>
                <label className="text-xs text-zinc-500">{label}</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid={testid} type="color" value={active[key]} onChange={(e) => set(key, e.target.value)} className="w-9 h-9 rounded-md bg-transparent border border-zinc-800 cursor-pointer" />
                  <Input className={`${field} font-num`} value={active[key]} onChange={(e) => set(key, e.target.value)} />
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500">Heading font</label>
              <select data-testid="brand-kit-heading-font" className={`mt-1 h-9 w-full rounded-md px-2 ${field}`} value={active.heading_font} onChange={(e) => set("heading_font", e.target.value)}>
                {meta.fonts.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500">Body font</label>
              <select data-testid="brand-kit-body-font" className={`mt-1 h-9 w-full rounded-md px-2 ${field}`} value={active.body_font} onChange={(e) => set("body_font", e.target.value)}>
                {meta.fonts.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-500">Cover style</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {meta.cover_styles.map((s) => (
                <button
                  key={s.key}
                  data-testid={`cover-style-${s.key}`}
                  onClick={() => set("cover_style", s.key)}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    active.cover_style === s.key ? "border-indigo-500/50 bg-indigo-500/10 text-indigo-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <span className="block font-medium">{s.label}</span>
                  <span className="block text-[10px] text-zinc-500 mt-0.5">{s.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500">Page size</label>
              <select data-testid="brand-kit-page-size" className={`mt-1 h-9 w-full rounded-md px-2 ${field}`} value={active.page_size} onChange={(e) => set("page_size", e.target.value)}>
                {meta.page_sizes.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500">Margin (mm)</label>
              <Input data-testid="brand-kit-margin-input" type="number" className={`mt-1 ${field} font-num`} value={active.margin_mm} onChange={(e) => set("margin_mm", Number(e.target.value))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500">Running header</label>
              <Input data-testid="brand-kit-header-input" className={`mt-1 ${field}`} value={active.header_text} onChange={(e) => set("header_text", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Footer</label>
              <Input data-testid="brand-kit-footer-input" className={`mt-1 ${field}`} value={active.footer_text} onChange={(e) => set("footer_text", e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input data-testid="brand-kit-page-numbers" type="checkbox" checked={!!active.show_page_numbers} onChange={(e) => set("show_page_numbers", e.target.checked)} />
              Show page numbers
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input data-testid="brand-kit-default-toggle" type="checkbox" checked={!!active.is_default} onChange={(e) => set("is_default", e.target.checked)} />
              Default for new proposals
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button data-testid="save-brand-kit-btn" onClick={save} disabled={busy} className="bg-indigo-500 hover:bg-indigo-600 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save brand kit"}
            </Button>
            <Button data-testid="print-sample-btn" variant="outline" onClick={() => window.print()} className="border-zinc-800 text-zinc-300">
              Download sample PDF
            </Button>
            {active.id && kits.length > 1 && (
              <Button data-testid="delete-brand-kit-btn" variant="outline" onClick={() => remove(active.id)} className="border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500 mb-3">Live document preview</p>
          <DocPreview proposal={SAMPLE} brand={active} orgName={user?.org_name} testid="brand-kit-preview" />
        </div>
      </div>
    </AppShell>
  );
}
